// Theme system: two independent axes applied as attributes on <html>.
//   data-scheme = plum|jade|ocean|rose|sunset   (brand/accent)
//   data-mode   = light|dark                     (surfaces/text)
// This module lives under src/ui/ (NOT src/engine/), so unlike the engine
// it is allowed to touch document / localStorage / matchMedia.

// `primary`/`accent` drive the picker swatches; the CSS holds the full ramp.
export const SCHEMES = [
  { id: 'plum',   label: 'Plum',   primary: '#5a2a82', accent: '#d667a3' },
  { id: 'jade',   label: 'Jade',   primary: '#157f6b', accent: '#c89b3c' },
  { id: 'ocean',  label: 'Ocean',  primary: '#1d5fa5', accent: '#2bb3c0' },
  { id: 'rose',   label: 'Rose',   primary: '#b03060', accent: '#e0876a' },
  { id: 'sunset', label: 'Sunset', primary: '#b5532a', accent: '#e0a23c' },
];
export const MODES = ['light', 'dark'];

const DEFAULT_SCHEME = 'plum';
const DEFAULT_MODE = 'light';
const SCHEME_IDS = SCHEMES.map((s) => s.id);
const STORAGE_KEY = 'mahjong.theme';

// Pure: given the raw localStorage string and the OS dark preference, decide
// the active theme. Invalid/partial input degrades to plum + prefers.
export function resolveTheme(storedJson, prefersDark) {
  let stored = null;
  try {
    stored = storedJson ? JSON.parse(storedJson) : null;
  } catch {
    stored = null;
  }
  const scheme = stored && SCHEME_IDS.includes(stored.scheme) ? stored.scheme : DEFAULT_SCHEME;
  const mode =
    stored && MODES.includes(stored.mode) ? stored.mode : prefersDark ? 'dark' : DEFAULT_MODE;
  return { scheme, mode };
}

export function applyTheme({ scheme, mode }) {
  const el = document.documentElement;
  el.setAttribute('data-scheme', scheme);
  el.setAttribute('data-mode', mode);
}

export function getTheme() {
  const el = document.documentElement;
  return {
    scheme: el.getAttribute('data-scheme') || DEFAULT_SCHEME,
    mode: el.getAttribute('data-mode') || DEFAULT_MODE,
  };
}

function loadStored() {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function saveTheme(theme) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(theme));
  } catch {
    // private mode / storage disabled: theme still applies for the session
  }
}

export function initTheme() {
  const mql =
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(prefers-color-scheme: dark)')
      : null;
  const prefersDark = mql ? mql.matches : false;
  const theme = resolveTheme(loadStored(), prefersDark);
  applyTheme(theme);
  return theme;
}

export function setScheme(scheme) {
  const theme = { ...getTheme(), scheme };
  applyTheme(theme);
  saveTheme(theme);
  return theme;
}

export function setMode(mode) {
  const theme = { ...getTheme(), mode };
  applyTheme(theme);
  saveTheme(theme);
  return theme;
}

// Build the picker: a floating palette button (hidden on the game screen via
// CSS), an icon injected into the in-game action bar, and one shared panel.
// Idempotent — safe to call once at startup.
export function mountThemePicker() {
  if (document.getElementById('themeFab')) return;

  const fab = document.createElement('button');
  fab.id = 'themeFab';
  fab.type = 'button';
  fab.className = 'theme-fab';
  fab.title = 'Appearance';
  fab.setAttribute('aria-label', 'Appearance');
  fab.textContent = '🎨';
  document.body.appendChild(fab);

  const panel = document.createElement('div');
  panel.id = 'themePanel';
  panel.className = 'theme-panel';
  panel.hidden = true;
  panel.innerHTML =
    '<div class="theme-panel-head">Appearance</div>' +
    '<div class="theme-row">' +
    '<span class="theme-row-label">Mode</span>' +
    '<div class="theme-seg" id="themeModeSeg">' +
    '<button type="button" data-mode="light">Light</button>' +
    '<button type="button" data-mode="dark">Dark</button>' +
    '</div></div>' +
    '<div class="theme-row theme-row-col">' +
    '<span class="theme-row-label">Color</span>' +
    '<div class="theme-swatches" id="themeSwatches"></div>' +
    '</div>';
  document.body.appendChild(panel);

  const swatches = panel.querySelector('#themeSwatches');
  SCHEMES.forEach((s) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'theme-swatch';
    b.dataset.scheme = s.id;
    b.title = s.label;
    b.setAttribute('aria-label', s.label);
    b.style.background = `linear-gradient(135deg, ${s.primary} 0 50%, ${s.accent} 50% 100%)`;
    swatches.appendChild(b);
  });

  // In-game trigger: inject an icon button into the existing action bar so the
  // floating fab can stay hidden during play (the bar is crowded).
  const acts = document.querySelector('#game .acts');
  let actsBtn = null;
  if (acts) {
    actsBtn = document.createElement('button');
    actsBtn.id = 'btnTheme';
    actsBtn.type = 'button';
    actsBtn.className = 'icon-only';
    actsBtn.title = 'Appearance';
    actsBtn.setAttribute('aria-label', 'Appearance');
    actsBtn.innerHTML = '<span class="icon">🎨</span>';
    acts.insertBefore(actsBtn, acts.firstChild);
  }

  const refresh = () => {
    const { scheme, mode } = getTheme();
    panel
      .querySelectorAll('#themeModeSeg button')
      .forEach((btn) => btn.classList.toggle('active', btn.dataset.mode === mode));
    panel
      .querySelectorAll('.theme-swatch')
      .forEach((btn) => btn.classList.toggle('selected', btn.dataset.scheme === scheme));
  };
  const open = () => {
    panel.hidden = false;
    refresh();
  };
  const close = () => {
    panel.hidden = true;
  };
  const toggle = () => (panel.hidden ? open() : close());

  fab.addEventListener('click', toggle);
  if (actsBtn) actsBtn.addEventListener('click', toggle);

  panel.querySelector('#themeModeSeg').addEventListener('click', (e) => {
    const btn = e.target.closest('button[data-mode]');
    if (!btn) return;
    setMode(btn.dataset.mode);
    refresh();
  });
  swatches.addEventListener('click', (e) => {
    const btn = e.target.closest('.theme-swatch');
    if (!btn) return;
    setScheme(btn.dataset.scheme);
    refresh();
  });

  document.addEventListener('click', (e) => {
    if (panel.hidden) return;
    if (panel.contains(e.target) || fab.contains(e.target) || (actsBtn && actsBtn.contains(e.target))) {
      return;
    }
    close();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') close();
  });
}

// Hide the picker panel if it's open. Called on screen navigation so the panel
// never floats over a screen the user navigated away to.
export function closeThemePicker() {
  const panel = document.getElementById('themePanel');
  if (panel) panel.hidden = true;
}
